/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { extractBackgroundContainerProps, extractSectionContainerProps } from "@components/utils/section.utils";

import {
	BackgroundContainer,
	CosmosTextVariantEnum,
	hasContent,
	LogoTitleDescButtonStack,
	ResponsiveContainer,
	SectionContainer,
} from "@components/elements";
import type { CosmosButtonProps } from "@components/elements";
import { HeadingLevelContext } from "@components/context";
import { css, cx } from "@panda/css";

import { OrionCards } from "./OrionCards.molecule";

import type { OrionCardsSectionFragment } from "@cosmos/hygraph-schema";

/**
 * Two columns from 1440px up (copy 656px / cards fill the rest), one stacked column below that.
 *
 * Every `flexDirection` / `width` value sits inside a media query. Leaving one in the base layer
 * and overriding it at a breakpoint puts the two rules in different Panda cascade layers, and the
 * base rule wins at every viewport — see the layer gotcha in libraries/cosmos-components/CLAUDE.md.
 */
const sectionStyles = css({
	display: "flex",
	alignItems: "flex-start",
	width: "100%",
	gap: "eds.dimension.48",
	xlDown: {
		flexDirection: "column",
	},
	xl: {
		flexDirection: "row",
	},
});

const copyColumnStyles = css({
	minW: "0",
	xlDown: {
		width: "100%",
	},
	xl: {
		width: "656px",
		flexShrink: "0",
	},
});

const cardsColumnStyles = css({
	minW: "0",
	xlDown: {
		width: "100%",
	},
	xl: {
		flexGrow: "1",
		flexBasis: "0",
	},
});

const headingStyles = css({
	"& .orion-section-heading .heading": {
		smDown: {
			textStyle: "headingXL",
		},
	},
	"& .logo-title-desc-button-stack .eyebrow": {
		textStyle: "headingSM",
		textTransform: "unset",
	},
	"& .logo img": {
		height: "32px",
		width: "auto",
	},
});

export type OrionCardsSectionProps = OrionCardsSectionFragment & { sectionIndex?: number; hasHero?: boolean };

export const OrionCardsSection = ({
	config,
	eyebrow,
	title,
	titleGradient,
	desc,
	modButtons,
	cards,
	sectionIndex,
	hasHero,
}: OrionCardsSectionProps): React.JSX.Element | null => {
	const validHeading = Boolean(eyebrow || hasContent(title) || desc || modButtons?.length);
	const validContent = Boolean(cards?.length);
	if (!validHeading && !validContent) return null;

	// When this is the first ref-section on a hero-less page, its title is the page h1
	// (cards then shift h3→h2); otherwise the title is an h2 nested under the hero/page h1.
	const isPageTitle = sectionIndex === 0 && !hasHero;

	// modButtons wins when authored, matching GeminiCard / HeroAlpha. Mapped to CosmosButtonProps
	// because LogoTitleDescButtonStack types its `buttons` that way — the spread carries `label`
	// / `href` / `variant` / `icon` straight through from either arm of the CosmosModButton union.
	const cosmosButtons = modButtons?.map((button): CosmosButtonProps => ({ ...button, size: "lg" }));

	return (
		<BackgroundContainer {...extractBackgroundContainerProps(config)} bgBlur>
			<ResponsiveContainer>
				<SectionContainer {...extractSectionContainerProps(config)}>
					<div className={cx("orion-card-section", sectionStyles, headingStyles)}>
						{validHeading ? (
							<div className={cx("orion-copy-column", copyColumnStyles)}>
								<LogoTitleDescButtonStack
									animate={false}
									stack={{ gap: "eds.dimension.24" }}
									eyebrow={eyebrow}
									buttons={cosmosButtons}
									titleDescStack={{
										stack: { gap: "eds.dimension.24", align: "start" },
										// Top-level ref-section title: the page h1 when it's the first hero-less section,
										// else h2 nested under the hero/page h1. Visual size is variant-driven.
										title: { ...title, variant: CosmosTextVariantEnum.heading2XL, as: isPageTitle ? "h1" : "h2" },
										gradient: titleGradient,
										description: {
											html: desc,
											variant: CosmosTextVariantEnum.paragraphMD,
											as: "div",
											color: "eds.text.secondary",
										},
										className: "orion-section-heading",
									}}
								/>
							</div>
						) : null}
						{validContent ? (
							<div className={cx("orion-cards-column", cardsColumnStyles)}>
								{/* Cards render h3. Shift up to h2 when the section title is the page h1, or when
								    there's no title, so the outline never skips (h1/…→h3). */}
								<HeadingLevelContext.Provider value={{ offset: isPageTitle ? -1 : hasContent(title) ? 0 : -1 }}>
									<OrionCards cards={cards || []} />
								</HeadingLevelContext.Provider>
							</div>
						) : null}
					</div>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
